'use strict';
import events from 'events';
import resources from './resources';
import * as errors from './Errors';
import Webhooks from './Webhooks';
import { pascalToCamelCase, validateInteger } from './utils';
import { BRASIL_SERVICE_TYPES, HONG_KONG_SERVICE_TYPES, INDONESIA_SERVICE_TYPES, MALAYSIA_SERVICE_TYPES, MARKETS, MEXICO_SERVICE_TYPES, PHILIPPINES_SERVICE_TYPES, SINGAPORE_SERVICE_TYPES, TAIWAN_SERVICE_TYPES, THAILAND_SERVICE_TYPES, UNITED_STATES_SERVICE_TYPES, VIET_NAM_SERVICE_TYPES, } from './data/markets';
const DEFAULT_HOST = 'rest.lalamove.com';
const SANDBOX_HOST = 'rest.sandbox.lalamove.com';
const DEFAULT_PORT = '443';
const DEFAULT_BASE_PATH = '/v2/';
const DEFAULT_TIMEOUT = 30000;
const ALLOWED_CONFIG_PROPERTIES = [
    'market',
    'sandbox',
    'httpAgent',
    'timeout',
    'host',
    'port',
    'protocol',
];
const EventEmitter = events.EventEmitter;
export default function Lalamove(apiKey, apiSecret, config = {}) {
    if (!(this instanceof Lalamove)) {
        return new Lalamove(apiKey, apiSecret, config);
    }
    const props = this._getPropsFromConfig(config);
    Object.defineProperty(this, '_emitter', {
        value: new EventEmitter(),
        enumerable: false,
        configurable: false,
        writable: false,
    });
    this.on = this._emitter.on.bind(this._emitter);
    this.once = this._emitter.once.bind(this._emitter);
    this.off = this._emitter.removeListener.bind(this._emitter);
    if (props.protocol &&
        props.protocol !== 'https' &&
        (!props.host || /\.lalamove\.com$/.test(props.host))) {
        throw new Error('The `https` protocol must be used when sending requests to `*.lalamove.com`');
    }
    this._api = {
        apiKey: null,
        apiSecret: null,
        market: props.market || null,
        host: props.host || (props.sandbox ? SANDBOX_HOST : DEFAULT_HOST),
        port: props.port || DEFAULT_PORT,
        protocol: props.protocol || 'https',
        basePath: DEFAULT_BASE_PATH,
        timeout: validateInteger('timeout', props.timeout, DEFAULT_TIMEOUT),
        agent: props.httpAgent || null,
    };
    this._prepResources();
    this._setApiField('apiKey', apiKey);
    this._setApiField('apiSecret', apiSecret);
    this.errors = errors;
    this.webhooks = Webhooks;
}
Lalamove.errors = errors;
Lalamove.webhooks = Webhooks;
Lalamove.MARKETS = MARKETS;
Lalamove.serviceTypesByMarket = function (market) {
    switch (market) {
        case "BR":
            return BRASIL_SERVICE_TYPES;
        case "HK":
            return HONG_KONG_SERVICE_TYPES;
        case "ID":
            return INDONESIA_SERVICE_TYPES;
        case "MY":
            return MALAYSIA_SERVICE_TYPES;
        case "MX":
            return MEXICO_SERVICE_TYPES;
        case "PH":
            return PHILIPPINES_SERVICE_TYPES;
        case "SG":
            return SINGAPORE_SERVICE_TYPES;
        case "TW":
            return TAIWAN_SERVICE_TYPES;
        case "TH":
            return THAILAND_SERVICE_TYPES;
        case "US":
            return UNITED_STATES_SERVICE_TYPES;
        case "VN":
            return VIET_NAM_SERVICE_TYPES;
        default:
            return [];
    }
};
Lalamove.localeByMarket = function (market) {
    switch (market) {
        case "BR":
            return "en_BR";
        case "HK":
            return "en_HK";
        case "ID":
            return "en_ID";
        case "MY":
            return "en_MY";
        case "MX":
            return "en_MX";
        case "PH":
            return "en_PH";
        case "TW":
            return "zh_TW";
        case "TH":
            return "en_TH";
        case "US":
            return "en_US";
        case "VN":
            return "en_VN";
        case "SG":
            return "en_SG";
        default:
            return "";
    }
};
Lalamove.prototype = {
    _prepResources() {
        for (const name in resources) {
            this[pascalToCamelCase(name)] = new resources[name](this);
        }
    },
    _setApiField(key, value) {
        this._api[key] = value;
    },
    getApiField(key) {
        return this._api[key];
    },
    _getPropsFromConfig(config) {
        if (!config) {
            return {};
        }
        const isObject = config === Object(config) && !Array.isArray(config);
        if (!isObject) {
            throw new Error('Config must either be an object');
        }
        const values = Object.keys(config).filter((value) => !ALLOWED_CONFIG_PROPERTIES.includes(value));
        if (values.length > 0) {
            throw new Error(`Config object may only contain the following: ${ALLOWED_CONFIG_PROPERTIES.join(', ')}`);
        }
        return config;
    }
};
//# sourceMappingURL=index.js.map